import { useMemo } from 'react';
import { LOCAL_ACCOUNTS_BASE_URL } from '../consts';
import { usePaginatedGet, UsePaginatedGetParams, UsePaginatedGetReturnType } from '../../../../api';
import { LocalAccount } from '../../types/LocalAccount';

export type LocalAccountsFilters = {
    search?: string;
};

export type LocalAccountsOrder = {
    orderBy: string | string[];
    orderDirection: 'asc' | 'desc';
};

interface Params {
    filters: LocalAccountsFilters;
    order: LocalAccountsOrder
}

type ReturnType = Omit<UsePaginatedGetReturnType<unknown>, 'data'> & {
    localAccounts?: LocalAccount[];
};

type LocalAccountsDto = {
    [Key in keyof LocalAccount]: LocalAccount[Key] extends Date
    ? Exclude<LocalAccount[Key], Date> | string
    : LocalAccount[Key];
};

const NUMBER_OF_LOCAL_ACCOUNTS_TO_FETCH = 100;

const convertFiltersToQueryParams = (filters: LocalAccountsFilters): UsePaginatedGetParams['queryParams'] => {
    const queryParams: UsePaginatedGetParams['queryParams'] = {};

    Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            if (value.length > 0) {
                queryParams[key] = value.toString();
            }
            return;
        }
        if (value !== undefined && value !== '') {
            queryParams[key] = value.toString();
        }
    });

    return queryParams;
};

const convertLocalAccountsDtoToLocalAccount = (localAccountResponse: LocalAccountsDto): LocalAccount => {
    const { lastLogon, passwordLastSet, excludeTill, updateDate, ...restOfValues } = localAccountResponse;

    return {
        ...restOfValues,
        updateDate: new Date(updateDate),
        lastLogon: lastLogon ? new Date(lastLogon) : undefined,
        passwordLastSet: passwordLastSet ? new Date(passwordLastSet) : undefined,
        excludeTill: excludeTill ? new Date(excludeTill) : undefined
    };
};

export const useGetLocalAccounts = ({ filters, order }: Params): ReturnType => {
    const { data: localAccountsResponse, ...restOfReturnValues } = usePaginatedGet<LocalAccountsDto>({
        url: LOCAL_ACCOUNTS_BASE_URL,
        limit: NUMBER_OF_LOCAL_ACCOUNTS_TO_FETCH,
        queryParams: { ...convertFiltersToQueryParams(filters), orderBy: Array.isArray(order.orderBy) ? order.orderBy.toString() : order.orderBy, orderDirection: order.orderDirection }
    });

    const localAccounts = useMemo(
        () => localAccountsResponse.map(convertLocalAccountsDtoToLocalAccount),
        [localAccountsResponse]
    );

    return {
        ...restOfReturnValues,
        localAccounts
    };
};
